// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React, { FC, useCallback } from "react";

import { useSwiper } from "swiper/react";

import { useArrowElement } from "@eisinc/App/Components/XngCarousel/hooks";
import { ArrowPosition, ArrowType, NavigationProps } from "@eisinc/App/Components/XngCarousel/models";
import IconPaginationSmallLeft from "@eisinc/XDL/Icons/IconPaginationSmallLeft";
import IconPaginationSmallRight from "@eisinc/XDL/Icons/IconPaginationSmallRight";
import { color, xClassNames, xScreen } from "@eisinc/XDL/Utilities";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const generateArrowStyle = (
    arrowPosition: ArrowPosition,
    showOnMobile: boolean,
    showOnTablet: boolean,
    hideArrowIfDisabled?: boolean,
    isNavigationOutSide?: boolean,
) => {
    const arrowDisplay = (show: boolean) => css`
        &.arrow-on-desktop {
            display: ${show ? "flex" : "none"};
        }
    `;

    const mobileArrowStyles = arrowDisplay(showOnMobile);
    const tabletArrowStyles = arrowDisplay(showOnTablet);

    if (arrowPosition === "top" || arrowPosition === "bottom") {
        return {
            size: 16,
            css: css`
                background-color: ${color("common.black", 0.5)};
                border: 2px solid ${color("common.white")};
                border-radius: 100%;
                width: 24px;
                height: 24px;
                line-height: 16px;
                margin: 0;
                display: flex;
                position: relative;
                z-index: 9;
                position: absolute;
                left: 0;
                margin: 0;
                span {
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    position: absolute;
                }
                svg {
                    color: ${color("common.white")};
                    transform: rotate(90deg);
                }
                ${arrowPosition === "top" &&
                css`
                    top: 9px;
                `};

                ${arrowPosition === "bottom" &&
                css`
                    bottom: 15px;
                `};

                &.swiper-button-disabled,
                &.swiper-button-lock {
                    ${hideArrowIfDisabled &&
                    css`
                        visibility: hidden;
                    `}
                    pointer-events: none; // Click through it, if for any reason we want to display it we need to do it on widget level.

                    cursor: not-allowed;
                    & > ${IconWrapper} {
                        color: ${color("common.ui4")};
                    }
                }
                ${xScreen("lte", "md")} {
                    ${tabletArrowStyles}
                }
                ${xScreen("lte", "sm")} {
                    ${mobileArrowStyles}
                }
            `,
        };
    }
    return {
        size: 16,
        css: css`
            ${isNavigationOutSide
                ? css`
                      & > ${IconWrapper} {
                          color: ${color("primary")};
                          width: 10px;
                          height: 18px;
                          transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
                          justify-content: center;
                          &,
                          & > svg {
                              width: 10px;
                              height: 18px;
                          }
                      }
                      &:not(:disabled):hover {
                          & > ${IconWrapper} {
                              color: ${color("common.ui8")};
                          }
                      }
                      &:disabled {
                          & > ${IconWrapper} {
                              color: ${color("common.diabled")};
                          }
                      }
                  `
                : css`
                      ${arrowPosition === "left" ? `margin-right` : `margin-left`}: 12px;

                      & > ${IconWrapper} {
                          color: ${color("common.white")};
                          background: ${color("common.black", 0.5)};
                          border: 2px solid ${color("common.white")};
                          border-radius: 100%;
                          width: 52px;
                          height: 52px;
                          transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
                          justify-content: center;
                          & > svg {
                              height: 23px;
                              width: 12px;
                          }
                      }
                      &:not(:disabled):hover {
                          & > ${IconWrapper} {
                              background: ${color("common.ui8")};
                              border: none;
                          }
                      }
                      &:disabled {
                          & > ${IconWrapper} {
                              opacity: 33%;
                          }
                      }
                  `}

            transform: none;
            z-index: 10;
            padding: 0;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 0;
            outline: none !important;
            pointer-events: all;

            &:focus {
                box-shadow: none;
            }

            &.swiper-button-disabled,
            &.swiper-button-lock {
                ${hideArrowIfDisabled &&
                css`
                    visibility: hidden;
                `}
                pointer-events: none; // Click through it, if for any reason we want to display it we need to do it on widget level.

                cursor: not-allowed;
                & > ${IconWrapper} {
                    color: ${color("common.ui4")};
                }
            }
            ${xScreen("lte", "md")} {
                ${tabletArrowStyles}
            }
            ${xScreen("lte", "sm")} {
                ${mobileArrowStyles}
            }
        `,
    };
};

const arrowNavigationIcons = {
    top: {
        src: ChevronLeft,
        size: 16,
    },
    bottom: {
        src: ChevronRight,
        size: 16,
    },
    left: {
        src: IconPaginationSmallLeft,
        size: 12,
    },
    right: {
        src: IconPaginationSmallRight,
        size: 12,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({
        arrowType,
        showOnMobile,
        showOnTablet,
        hideArrowIfDisabled,
        buttonIconProps,
        navigationOutSide,
        ...otherProps
    }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            showOnTablet,
            className: xClassNames.append(className, otherProps.className),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(
                    arrowPosition,
                    showOnMobile,
                    showOnTablet,
                    hideArrowIfDisabled ?? false,
                    navigationOutSide,
                )}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
                data-test-selector={`carousel-${arrowPosition}-arrow-button`}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled ? "disabled" : ""}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
